import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, defer, NEVER, type Observable } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { sseTurquoiseColor } from '../../../constants/color';
import type {
    BusinessIntelligenceDataPoint,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from '../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { UnitCode } from '../../../interfaces/Unit.interface';
import { isDataPointMeaningfulInChart } from '../../../providers/business-intelligence/business-intelligence.utils';
import { HighchartsStaticProvider } from '../../../providers/highcharts/highcharts.static.provider';
import { UnitProvider } from '../../../providers/unit/unit.provider';
import { HighchartsChartComponent } from '../../highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';

export interface BusinessIntelligencePieChartComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    measureCode: BusinessIntelligenceMeasureCode;
    segmentationDimensionCode: BusinessIntelligenceDimensionCode;
    areDataLabelsActivated: boolean;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-pie-chart.scss'],
    selector: 'business-intelligence-pie-chart',
    templateUrl: 'business-intelligence-pie-chart.html',
    imports: [HighchartsChartComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligencePieChartComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligencePieChartComponentConfig>
    implements OnInit
{
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _unitProvider = inject(UnitProvider);

    noData: boolean = false;

    tooltip$ = new BehaviorSubject<{ tooltip: any; content$: Observable<string> } | null>(null);

    chartConfig$!: Observable<Highcharts.Options>;

    ngOnInit() {
        const percentFormatter = this._unitProvider.formatterFactory(UnitCode.PERCENT, UnitCode.PERCENT);

        this.tooltip$
            .pipe(
                filter(isNotNullish),
                debounceTime(0),
                switchMap(({ tooltip, content$ }) => content$.pipe(map(content => ({ tooltip, content })))),
                untilDestroyed(this)
            )
            .subscribe(({ tooltip, content }) => {
                tooltip.label.attr({
                    text: content
                });
            });
        const tooltip$ = this.tooltip$;

        this.chartConfig$ = combineLatest([this._representationData$, this._config$]).pipe(
            filter(
                ([representationData, config]) =>
                    representationData && !!Object.keys(representationData.metadata).length && isNotNullish(config)
            ),
            tap(([representationData, config]) => {
                const meaningfulData = representationData.data.filter(
                    d => isNotNullish(d.measures[config.measureCode]) && d.measures[config.measureCode] !== 0
                );

                this.noData = !meaningfulData.length;
            }),
            switchMap(([representationData, config]) => {
                return defer(() => {
                    const meaningfulData = representationData.data.filter(isDataPointMeaningfulInChart([config.measureCode]));
                    const generateSeriesData = (seriesData: BusinessIntelligenceDataPoint[]) => {
                        if (!seriesData || seriesData.length === 0) {
                            return [];
                        }
                        if (config.segmentationDimensionCode) {
                            return seriesData
                                .slice()
                                .sort((dp1, dp2) => dp1.measures[config.measureCode] - dp2.measures[config.measureCode])
                                .map(dp => {
                                    return {
                                        name: representationData.metadata[
                                            config.segmentationDimensionCode
                                        ].valuesFormatting.formatAsStandaloneText$(dp.dimensions[config.segmentationDimensionCode]),
                                        y: dp.measures[config.measureCode],
                                        color: representationData.metadata[
                                            config.segmentationDimensionCode
                                        ].valuesFormatting.formatAsColor$(dp.dimensions[config.segmentationDimensionCode])
                                    };
                                });
                        } else {
                            return [
                                {
                                    name: representationData.metadata[config.measureCode].getName$(),
                                    y: seriesData[0].measures[config.measureCode],
                                    color: sseTurquoiseColor
                                }
                            ];
                        }
                    };
                    const series = [
                        {
                            name: representationData.metadata[config.measureCode].getName$(),
                            colorByPoint: true,
                            data: generateSeriesData(meaningfulData),
                            showInLegend: true
                        }
                    ];
                    return combineLatestDeepObject(
                        this._highchartsStaticProvider.buildConfig({
                            chart: {
                                type: 'pie'
                            },
                            plotOptions: {
                                pie: {
                                    allowPointSelect: false,
                                    cursor: 'pointer',
                                    dataLabels: {
                                        enabled: config.areDataLabelsActivated ? config.areDataLabelsActivated : false,
                                        distance: -25,
                                        format: '{point.name}<br/>{point.percentage:.0f}%',
                                        style: {
                                            textOutline: 'none',
                                            textAlign: 'center'
                                        },
                                        allowOverlap: true
                                    }
                                }
                            },
                            tooltip: {
                                formatter(this: TooltipFormatterContext, tooltip: any) {
                                    tooltip$.next({
                                        tooltip,
                                        content$: combineLatest([
                                            representationData.metadata[config.measureCode].valuesFormatting.formatAsStandaloneText$(
                                                this.point.y
                                            )
                                        ]).pipe(
                                            map(
                                                ([pointValue]) =>
                                                    `${this.point.name}<br/>
                                                        <b>${pointValue}</b> (${percentFormatter(this.point.percentage as number)})`
                                            )
                                        )
                                    });
                                    return;
                                }
                            },
                            legend: {
                                enabled: true
                            },
                            series
                        })
                    );
                }).pipe(
                    catchError(err => {
                        console.error('Error while updating pie chart config ::', err);
                        return NEVER;
                    })
                );
            })
        );
    }

    getExportMembers(config: BusinessIntelligencePieChartComponentConfig): BusinessIntelligenceMemberCode[] {
        return [config.segmentationDimensionCode, config.measureCode];
    }
}
